import { Badge, Card, Flex, Tag, Typography, theme } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { CompanyEntry, OpeningHoursSlot } from '../api';

const { Text } = Typography;

/** Format an array of opening-hours slots into a human-readable string; `closedLabel` is used for closed days. */
function formatTimeSlots(slots: OpeningHoursSlot[], closedLabel: string): string {
  if (!slots.length) return closedLabel;
  if (slots[0].isClosed) return closedLabel;
  return slots
    .filter((s) => !s.isClosed && s.fromTime && s.toTime)
    .map((s) => `${s.fromTime} – ${s.toTime}`)
    .join(', ');
}

interface OpeningHoursCardProps {
  openinghours: NonNullable<CompanyEntry['openinghours']>;
}

/** Displays the weekly opening hours in a clean list, highlighting today. */
export function OpeningHoursCard({ openinghours }: OpeningHoursCardProps) {
  const { token } = theme.useToken();
  const { t } = useTranslation('knowledge-base-module');
  const today = dayjs().format('dddd'); // e.g. "Monday"

  return (
    <Card
      title={
        <Flex align="center" gap={token.marginXS}>
          <ClockCircleOutlined style={{ color: token.colorPrimary }} />
          <span>{t('openingHours.title')}</span>
        </Flex>
      }
      style={{ height: '100%' }}
    >
      {Object.entries(openinghours).map(([day, slots], idx, arr) => {
        const isClosed = slots[0]?.isClosed;
        const isToday = day === today;
        return (
          <Flex
            key={day}
            justify="space-between"
            align="center"
            style={{
              padding: `${token.paddingXS}px 0`,
              gap: token.marginSM,
              borderBottom: idx < arr.length - 1 ? `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}` : undefined,
            }}
          >
            <Text strong={isToday}>
              {isToday && <Badge status="processing" style={{ marginInlineEnd: token.marginXS }} />}
              {day}
            </Text>
            {isClosed ? (
              <Tag color="default">{t('openingHours.closed')}</Tag>
            ) : (
              <Text>{formatTimeSlots(slots, t('openingHours.closed'))}</Text>
            )}
          </Flex>
        );
      })}
    </Card>
  );
}

export default OpeningHoursCard;
